/** @jsxImportSource react */

import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ToastContainer, { ToastContainerProps } from './ToastContainer';

export type Truncate = 'line-clamp-1' | 'line-clamp-2' | 'line-clamp-3';

export type ToastProviderProps = {
  children: React.ReactNode;
} & ToastContainerProps;

type ToastMessageType = 'Info' | 'Success' | 'Warning' | 'Error';

export type Toast = {
  id: string;
  lifetime: number;
  message: string | React.ReactNode;
  type?: ToastMessageType;
  truncate?: Truncate;
  Icon?: React.ReactNode | any;
  header?: string;
};

export type ToastContextType = {
  data: Array<Toast>;
  pushError(message: string, lifetime?: number, truncate?: Truncate): void;
  pushWarning(message: string, lifetime?: number, truncate?: Truncate): void;
  pushSuccess(message: string, lifetime?: number, truncate?: Truncate): void;
  pushInfo(message: string, lifetime?: number, truncate?: Truncate): void;
  push(
    message: string,
    type: ToastMessageType,
    lifetime?: number,
    truncate?: Truncate
  ): void;
  pushCustom(
    message: string | React.ReactNode,
    lifetime: number,
    truncate?: Truncate,
    Icon?: React.ReactNode
  ): void;
  remove(id: string): void;
};

export const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);

const DEFAULT_INTERVAL = 4000;

export default function ToastProvider({
  children,
  variant,
}: ToastProviderProps) {
  const [data, setData] = useState<Array<Toast>>([]);

  const Push = useCallback(
    (
      message: string,
      type: ToastMessageType,
      lifetime?: number,
      truncate?: Truncate
    ) => {
      if (message) {
        const new_item: Toast = {
          id: uuidv4(),
          message: message,
          type: type,
          lifetime: lifetime ? lifetime : DEFAULT_INTERVAL,
          truncate: truncate,
        };

        setData((prevState) => [...prevState, new_item]);
      }
    },
    [setData, data]
  );

  const PushCustom = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
      Icon?: React.ReactNode
    ) => {
      if (message) {
        const new_item: Toast = {
          id: uuidv4(),
          message: message,
          lifetime: lifetime ? lifetime : DEFAULT_INTERVAL,
          truncate: truncate,
          Icon: Icon,
          type: undefined,
        };

        setData((prevState) => [...prevState, new_item]);
      }
    },
    [setData, data]
  );

  const PushError = useCallback(
    (message: string, lifetime?: number, truncate?: Truncate) =>
      Push(message, 'Error', lifetime, truncate),
    [Push]
  );
  const PushWarning = useCallback(
    (message: string, lifetime?: number, truncate?: Truncate) =>
      Push(message, 'Warning', lifetime, truncate),
    [Push]
  );
  const PushSuccess = useCallback(
    (message: string, lifetime?: number, truncate?: Truncate) =>
      Push(message, 'Success', lifetime, truncate),
    [Push]
  );
  const PushInfo = useCallback(
    (message: string, lifetime?: number, truncate?: Truncate) =>
      Push(message, 'Info', lifetime, truncate),
    [Push]
  );

  const ToastContextFunction = useCallback(() => {
    return {
      data: data,
      pushError: PushError,
      pushWarning: PushWarning,
      pushSuccess: PushSuccess,
      pushInfo: PushInfo,
      push: Push,
      pushCustom: PushCustom,

      async remove(id: string) {
        setData((prevState) => prevState.filter((e) => e.id != id));
      },
    };
  }, [
    data,
    setData,
    PushError,
    PushWarning,
    PushSuccess,
    PushInfo,
    Push,
    PushCustom,
  ]);

  return (
    <ToastContext.Provider value={ToastContextFunction()}>
      <ToastContainer variant={variant} />
      {children}
    </ToastContext.Provider>
  );
}
